import { Checklist, Group, Section } from '@/services/types/checklist.types';
import { QuestionResponse, GroupedResponses, SectionResponses } from '../types/checklist-response.types';

/**
 * Agrupa respostas pela estrutura atual do checklist (match por questionId)
 */
function groupByChecklistStructure(
  responses: QuestionResponse[],
  checklist: Checklist
): GroupedResponses[] {
  const grouped: GroupedResponses[] = [];

  const responseMap = new Map<string, QuestionResponse>();
  responses.forEach((response) => {
    responseMap.set(response.questionId, response);
  });

  checklist.groups.forEach((group: Group) => {
    const sections: SectionResponses[] = [];

    group.sections.forEach((section: Section) => {
      const sectionResponses: QuestionResponse[] = [];

      section.questions.forEach((question) => {
        const response = responseMap.get(question.id);
        if (response) {
          sectionResponses.push({
            ...response,
            question: {
              ...response.question,
              groupId: group.id,
              groupName: group.name,
              sectionId: section.id,
              sectionName: section.name,
              order: question.order,
            },
          });
        }
      });

      if (sectionResponses.length > 0) {
        sections.push({
          sectionId: section.id,
          sectionName: section.name,
          sectionOrder: section.order,
          responses: sectionResponses.sort((a, b) => (a.question.order || 0) - (b.question.order || 0)),
        });
      }
    });

    if (sections.length > 0) {
      grouped.push({
        groupId: group.id,
        groupName: group.name,
        groupOrder: group.order,
        sections: sections.sort((a, b) => a.sectionOrder - b.sectionOrder),
      });
    }
  });

  return grouped.sort((a, b) => a.groupOrder - b.groupOrder);
}

/**
 * Agrupa respostas pelos dados da própria resposta (para checklist atualizado após preenchimento).
 * Usa groupName/sectionName retornados pelo backend junto com cada resposta.
 */
function groupByResponseData(responses: QuestionResponse[]): GroupedResponses[] {
  const groupMap = new Map<string, { group: GroupedResponses; sectionMap: Map<string, SectionResponses> }>();

  responses.forEach((response) => {
    const groupId = response.question.groupId || 'unknown-group';
    const groupName = response.question.groupName || 'Grupo';
    const groupOrder = response.question.groupOrder ?? 0;
    const sectionId = response.question.sectionId || 'unknown-section';
    const sectionName = response.question.sectionName || 'Seção';
    const sectionOrder = response.question.sectionOrder ?? 0;

    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        group: { groupId, groupName, groupOrder, sections: [] },
        sectionMap: new Map(),
      });
    }

    const entry = groupMap.get(groupId)!;

    if (!entry.sectionMap.has(sectionId)) {
      const section: SectionResponses = { sectionId, sectionName, sectionOrder, responses: [] };
      entry.sectionMap.set(sectionId, section);
    }

    entry.sectionMap.get(sectionId)!.responses.push(response);
  });

  const grouped: GroupedResponses[] = [];

  groupMap.forEach(({ group, sectionMap }) => {
    const sections: SectionResponses[] = [];
    sectionMap.forEach((section) => {
      section.responses.sort((a, b) => (a.question.order || 0) - (b.question.order || 0));
      sections.push(section);
    });
    group.sections = sections.sort((a, b) => a.sectionOrder - b.sectionOrder);
    grouped.push(group);
  });

  return grouped.sort((a, b) => a.groupOrder - b.groupOrder);
}

/**
 * Organiza as respostas do checklist por grupos e seções.
 * Primeiro tenta agrupar pela estrutura atual do checklist.
 * Se não encontrar matches (checklist foi atualizado após preenchimento),
 * agrupa pelos dados de grupo/seção retornados junto com cada resposta.
 */
export function groupChecklistResponses(
  responses: QuestionResponse[],
  checklist: Checklist
): GroupedResponses[] {
  if (!responses || responses.length === 0) return [];

  // Tentar agrupar pela estrutura atual do checklist
  const grouped = groupByChecklistStructure(responses, checklist);

  // Se encontrou matches, retorna
  if (grouped.length > 0) return grouped;

  // Fallback: agrupar pelos dados da própria resposta (histórico)
  return groupByResponseData(responses);
}
